import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState, useEffect } from 'react';
import { Box, Button, Checkbox, DialogContent, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField, Typography, Tabs, Tab, Autocomplete, Popper } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import ModalWidget from '../ModalWidget/ModalWidget';
import { useSearchUsersQuery } from '../../services/usersSlice';
import { User } from '../../../shared/types/User';
import { debounce } from 'lodash';

export interface FindMatch {
  rowId: number;
  column: string;
  value: string;
}

export interface FindInTableColumn {
  key: string;
  label: string;
}

export interface FindInTableProps<T> {
  isOpen: boolean;
  onClose: () => void;
  data: T[];
  columns: FindInTableColumn[];
  onMatchFound: (match: FindMatch) => void;
  getRowId: (row: T) => number;
  onReplace?: (rowId: number, column: string, newValue: string) => void;
  onReplaceAll?: (replacements: Array<{ rowId: number; column: string; newValue: string }>) => void;
}

function FindInTable<T extends Record<string, any>>({
  isOpen,
  onClose,
  data,
  columns,
  onMatchFound,
  getRowId,
  onReplace,
  onReplaceAll
}: FindInTableProps<T>) {
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [searchColumn, setSearchColumn] = useState<string>('all');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [matches, setMatches] = useState<FindMatch[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [replaceUserSearchTerm, setReplaceUserSearchTerm] = useState('');
  const [debouncedReplaceUserSearch, setDebouncedReplaceUserSearch] = useState('');

  // Fetch users for autocomplete (Find field)
  const { data: userResults = [] } = useSearchUsersQuery(
    { search: debouncedUserSearch },
    { skip: debouncedUserSearch.trim() === '' }
  );

  // Fetch users for autocomplete (Replace field)
  const { data: replaceUserResults = [] } = useSearchUsersQuery(
    { search: debouncedReplaceUserSearch },
    { skip: debouncedReplaceUserSearch.trim() === '' }
  );

  // Debounce user search (Find field)
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedUserSearch(userSearchTerm);
    }, 300);
    handler();
    return () => handler.cancel();
  }, [userSearchTerm]);

  // Debounce user search (Replace field)
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedReplaceUserSearch(replaceUserSearchTerm);
    }, 300);
    handler();
    return () => handler.cancel();
  }, [replaceUserSearchTerm]);

  const handleFind = () => {
    if (!findText || !data) {
      setMatches([]);
      return;
    }

    const searchValue = caseSensitive ? findText : findText.toLowerCase();
    const foundMatches: FindMatch[] = [];

    const columnsToSearch = searchColumn === 'all' 
      ? columns.map(col => col.key)
      : [searchColumn];

    data.forEach((row) => {
      columnsToSearch.forEach((col) => {
        const cellValue = (row[col] as string) || '';
        const compareValue = caseSensitive ? cellValue : cellValue.toLowerCase();
        
        if (compareValue.includes(searchValue)) {
          foundMatches.push({
            rowId: getRowId(row),
            column: col,
            value: cellValue
          });
        }
      });
    });

    setMatches(foundMatches);
    setCurrentMatchIndex(foundMatches.length > 0 ? 0 : -1);

    // Navigate to the first match
    if (foundMatches.length > 0) {
      onMatchFound(foundMatches[0]);
    }
  };

  const handleNextMatch = () => {
    if (matches.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(nextIndex);
    onMatchFound(matches[nextIndex]);
  };

  const handlePreviousMatch = () => {
    if (matches.length === 0) return;
    const prevIndex = currentMatchIndex === 0 ? matches.length - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(prevIndex);
    onMatchFound(matches[prevIndex]);
  };

  const handleClose = () => {
    setFindText('');
    setReplaceText('');
    setMatches([]);
    setCurrentMatchIndex(0);
    onClose();
  };

  const handleReplace = () => {
    if (!onReplace || matches.length === 0 || currentMatchIndex === -1) return;
    
    const currentMatch = matches[currentMatchIndex];
    // Escape special regex characters in findText
    const escapedFindText = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedFindText, caseSensitive ? 'g' : 'gi');
    const newValue = currentMatch.value.replace(regex, replaceText);
    
    onReplace(currentMatch.rowId, currentMatch.column, newValue);
    
    // Remove the replaced match from the list and navigate to next
    const newMatches = matches.filter((_, index) => index !== currentMatchIndex);
    setMatches(newMatches);
    
    if (newMatches.length > 0) {
      const nextIndex = currentMatchIndex >= newMatches.length ? 0 : currentMatchIndex;
      setCurrentMatchIndex(nextIndex);
      onMatchFound(newMatches[nextIndex]);
    } else {
      setCurrentMatchIndex(-1);
    }
  };

  const handleReplaceAll = () => {
    if (!onReplaceAll || matches.length === 0) return;
    
    // Escape special regex characters in findText
    const escapedFindText = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedFindText, caseSensitive ? 'g' : 'gi');
    
    const replacements = matches.map(match => {
      const newValue = match.value.replace(regex, replaceText);
      return {
        rowId: match.rowId,
        column: match.column,
        newValue
      };
    });
    
    onReplaceAll(replacements);
    
    // Clear matches after replace all
    setMatches([]);
    setCurrentMatchIndex(-1);
  };

  return (
    <ModalWidget
      isOpen={isOpen}
      onClose={handleClose}
      title="Find and Replace"
      positionTopRight={true}
      children={
        <DialogContent>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label="Find" sx={{ textTransform: 'none' }} />
            <Tab label="Replace" sx={{ textTransform: 'none' }} />
          </Tabs>

          {/* Find Tab */}
          {activeTab === 0 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  freeSolo
                  options={userResults}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.email || '';
                  }}
                  inputValue={findText}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'input') {
                      setFindText(newValue);
                      setUserSearchTerm(newValue);
                    } else if (reason === 'clear') {
                      setFindText('');
                      setUserSearchTerm('');
                    }
                  }}
                  onChange={(event, value) => {
                    if (value && typeof value !== 'string') {
                      setFindText(value.email || '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: typographyTokens.fontWeights.medium }}>
                          {option.displayName || `${option.givenName} ${option.surname}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#2372B9' }}>
                          {option.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  PopperComponent={(props) => <Popper {...props} placement="bottom-start" />}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoFocus
                      size="small"
                      label="Find what"
                      placeholder="Enter text or type a name for email..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleFind();
                        }
                      }}
                    />
                  )}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Search in Column</InputLabel>
                  <Select
                    value={searchColumn}
                    label="Search in Column"
                    onChange={(e) => setSearchColumn(e.target.value)}
                  >
                    <MenuItem value="all">All Columns</MenuItem>
                    {columns.map((col) => (
                      <MenuItem key={col.key} value={col.key}>
                        {col.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={caseSensitive}
                      onChange={(e) => setCaseSensitive(e.target.checked)}
                    />
                  }
                  label="Case Sensitive"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleFind}
                  sx={{ textTransform: 'none', flex: 1 }}
                >
                  Find All
                </Button>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                {matches.length > 0 && (
                  <>
                    <IconButton onClick={handlePreviousMatch} size="small">
                      <NavigateBeforeIcon />
                    </IconButton>
                    <Typography variant="body2">
                      {currentMatchIndex + 1} of {matches.length}
                    </Typography>
                    <IconButton onClick={handleNextMatch} size="small">
                      <NavigateNextIcon />
                    </IconButton>
                  </>
                )}
              </Box>

              {matches.length === 0 && findText && (
                <Typography color="text.secondary" variant="body2">
                  No matches found
                </Typography>
              )}
            </>
          )}

          {/* Replace Tab */}
          {activeTab === 1 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  freeSolo
                  options={userResults}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.email || '';
                  }}
                  inputValue={findText}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'input') {
                      setFindText(newValue);
                      setUserSearchTerm(newValue);
                    } else if (reason === 'clear') {
                      setFindText('');
                      setUserSearchTerm('');
                    }
                  }}
                  onChange={(event, value) => {
                    if (value && typeof value !== 'string') {
                      setFindText(value.email || '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: typographyTokens.fontWeights.medium }}>
                          {option.displayName || `${option.givenName} ${option.surname}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#2372B9' }}>
                          {option.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  PopperComponent={(props) => <Popper {...props} placement="bottom-start" />}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoFocus
                      size="small"
                      label="Find what"
                      placeholder="Enter text or type a name for email..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleFind();
                        }
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  freeSolo
                  options={replaceUserResults}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.email || '';
                  }}
                  inputValue={replaceText}
                  onInputChange={(event, newValue, reason) => {
                    if (reason === 'input') {
                      setReplaceText(newValue);
                      setReplaceUserSearchTerm(newValue);
                    } else if (reason === 'clear') {
                      setReplaceText('');
                      setReplaceUserSearchTerm('');
                    }
                  }}
                  onChange={(event, value) => {
                    if (value && typeof value !== 'string') {
                      setReplaceText(value.email || '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: typographyTokens.fontWeights.medium }}>
                          {option.displayName || `${option.givenName} ${option.surname}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#2372B9' }}>
                          {option.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  PopperComponent={(props) => <Popper {...props} placement="bottom-start" />}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Replace with"
                      placeholder="Enter replacement text or type a name for email..."
                    />
                  )}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Search in Column</InputLabel>
                  <Select
                    value={searchColumn}
                    label="Search in Column"
                    onChange={(e) => setSearchColumn(e.target.value)}
                  >
                    <MenuItem value="all">All Columns</MenuItem>
                    {columns.map((col) => (
                      <MenuItem key={col.key} value={col.key}>
                        {col.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={caseSensitive}
                      onChange={(e) => setCaseSensitive(e.target.checked)}
                    />
                  }
                  label="Case Sensitive"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleFind}
                  sx={{ textTransform: 'none', flex: '1 1 45%' }}
                >
                  Find All
                </Button>
                
                {onReplace && (
                  <Button
                    variant="outlined"
                    onClick={handleReplace}
                    disabled={matches.length === 0}
                    sx={{ textTransform: 'none', flex: '1 1 45%' }}
                  >
                    Replace
                  </Button>
                )}

                {onReplaceAll && (
                  <Button
                    variant="outlined"
                    onClick={handleReplaceAll}
                    disabled={matches.length === 0}
                    sx={{ textTransform: 'none', flex: '1 1 100%' }}
                  >
                    Replace All
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                {matches.length > 0 && (
                  <>
                    <IconButton onClick={handlePreviousMatch} size="small">
                      <NavigateBeforeIcon />
                    </IconButton>
                    <Typography variant="body2">
                      {currentMatchIndex + 1} of {matches.length}
                    </Typography>
                    <IconButton onClick={handleNextMatch} size="small">
                      <NavigateNextIcon />
                    </IconButton>
                  </>
                )}
              </Box>

              {matches.length === 0 && findText && (
                <Typography color="text.secondary" variant="body2">
                  No matches found
                </Typography>
              )}
            </>
          )}
        </DialogContent>
      }
    />
  );
}

export default FindInTable;
